import * as THREE from 'three';
import { Experience } from '../data/experiences';

export class Hologram {
  public group: THREE.Group = new THREE.Group();

  private projectorBase: THREE.Mesh;
  private holoCarGroup: THREE.Group = new THREE.Group();
  private holoIdeGroup: THREE.Group = new THREE.Group();
  private holoFpsGroup: THREE.Group = new THREE.Group();
  private beamMesh: THREE.Mesh;
  private currentMode: 'racer' | 'ide' | 'fps' = 'racer';
  private time: number = 0;

  constructor() {
    this.group.position.set(-1.15, 0.1, -0.1);

    // Projector Puck Base on Desk
    const baseGeo = new THREE.CylinderGeometry(0.2, 0.24, 0.05, 24);
    const baseMat = new THREE.MeshStandardMaterial({
      color: 0x141e2e,
      metalness: 0.9,
      roughness: 0.2,
    });
    this.projectorBase = new THREE.Mesh(baseGeo, baseMat);
    this.group.add(this.projectorBase);

    // Glowing Emitter Ring
    const ringGeo = new THREE.RingGeometry(0.12, 0.16, 24);
    const ringMat = new THREE.MeshBasicMaterial({
      color: 0x00fff2,
      side: THREE.DoubleSide,
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.026;
    this.group.add(ring);

    // Conical Holographic Light Beam
    const beamGeo = new THREE.ConeGeometry(0.5, 0.7, 16, 1, true);
    const beamMat = new THREE.MeshBasicMaterial({
      color: 0x00fff2,
      transparent: true,
      opacity: 0.12,
      side: THREE.DoubleSide,
    });
    this.beamMesh = new THREE.Mesh(beamGeo, beamMat);
    this.beamMesh.position.y = 0.35;
    this.group.add(this.beamMesh);

    // Build Wireframe Car Hologram for Racer
    this.buildHoloCar();

    // Build Wireframe Tesseract Hologram for IDE
    this.buildHoloIde();

    // Build Wireframe Skyscraper Spire for FPS
    this.buildHoloFps();

    this.group.add(this.holoCarGroup);
    this.group.add(this.holoIdeGroup);
    this.group.add(this.holoFpsGroup);

    this.setMode('racer');
  }

  private buildHoloCar() {
    const mat = new THREE.MeshBasicMaterial({
      color: 0xff007f,
      wireframe: true,
      transparent: true,
      opacity: 0.85,
    });

    // Car Body Wireframe
    const carBody = new THREE.Mesh(new THREE.BoxGeometry(0.65, 0.16, 0.32), mat);
    this.holoCarGroup.add(carBody);

    // Cabin
    const cabin = new THREE.Mesh(new THREE.BoxGeometry(0.35, 0.14, 0.26), mat);
    cabin.position.set(-0.05, 0.12, 0);
    this.holoCarGroup.add(cabin);

    // Wheels
    const wheelMat = new THREE.MeshBasicMaterial({
      color: 0x00fff2,
      wireframe: true,
      transparent: true,
      opacity: 0.9,
    });
    const wheelGeo = new THREE.CylinderGeometry(0.08, 0.08, 0.05, 12);
    [
      [-0.2, -0.07, 0.16],
      [0.2, -0.07, 0.16],
      [-0.2, -0.07, -0.16],
      [0.2, -0.07, -0.16],
    ].forEach(([x, y, z]) => {
      const wheel = new THREE.Mesh(wheelGeo, wheelMat);
      wheel.position.set(x, y, z);
      wheel.rotation.x = Math.PI / 2;
      this.holoCarGroup.add(wheel);
    });

    this.holoCarGroup.position.set(0, 0.45, 0);
  }

  private buildHoloIde() {
    const mat = new THREE.MeshBasicMaterial({
      color: 0x00fff2,
      wireframe: true,
      transparent: true,
      opacity: 0.85,
    });

    // Outer Octahedron
    const octa = new THREE.Mesh(new THREE.OctahedronGeometry(0.24, 0), mat);
    this.holoIdeGroup.add(octa);

    // Inner Icosahedron
    const innerMat = new THREE.MeshBasicMaterial({
      color: 0x39ff14,
      wireframe: true,
      transparent: true,
      opacity: 0.9,
    });
    const inner = new THREE.Mesh(new THREE.IcosahedronGeometry(0.14, 0), innerMat);
    this.holoIdeGroup.add(inner);

    // Orbiting Data Rings
    const ringMat = new THREE.MeshBasicMaterial({
      color: 0x00fff2,
      transparent: true,
      opacity: 0.4,
      side: THREE.DoubleSide,
    });
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.32, 0.008, 8, 32), ringMat);
    ring.rotation.x = Math.PI / 3;
    this.holoIdeGroup.add(ring);

    this.holoIdeGroup.position.set(0, 0.45, 0);
  }

  private buildHoloFps() {
    const mat = new THREE.MeshBasicMaterial({
      color: 0xffaa00,
      wireframe: true,
      transparent: true,
      opacity: 0.85,
    });

    // Central Skyscraper Tower Wireframe
    const tower = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.45, 0.24), mat);
    this.holoFpsGroup.add(tower);

    // Helipad / Jump Pad Ring
    const padMat = new THREE.MeshBasicMaterial({
      color: 0x00fff2,
      wireframe: true,
      transparent: true,
      opacity: 0.9,
    });
    const pad = new THREE.Mesh(new THREE.TorusGeometry(0.18, 0.01, 8, 24), padMat);
    pad.position.y = 0.23;
    pad.rotation.x = Math.PI / 2;
    this.holoFpsGroup.add(pad);

    // Spire Needle
    const spireMat = new THREE.MeshBasicMaterial({ color: 0xff3300 });
    const spire = new THREE.Mesh(new THREE.CylinderGeometry(0.005, 0.015, 0.25, 6), spireMat);
    spire.position.y = 0.35;
    this.holoFpsGroup.add(spire);

    this.holoFpsGroup.position.set(0, 0.45, 0);
  }

  public setMode(mode: 'racer' | 'ide' | 'fps') {
    this.currentMode = mode;
    this.holoCarGroup.visible = mode === 'racer';
    this.holoIdeGroup.visible = mode === 'ide';
    this.holoFpsGroup.visible = mode === 'fps';

    const beamColor = mode === 'racer' ? 0xff007f : mode === 'ide' ? 0x00fff2 : 0xffaa00;
    (this.beamMesh.material as THREE.MeshBasicMaterial).color.setHex(beamColor);
  }

  public update(deltaTime: number) {
    this.time += deltaTime;

    // Bobbing and rotating
    const bob = Math.sin(this.time * 2.5) * 0.03;

    if (this.currentMode === 'racer') {
      this.holoCarGroup.rotation.y += deltaTime * 1.4;
      this.holoCarGroup.position.y = 0.45 + bob;
    } else if (this.currentMode === 'ide') {
      this.holoIdeGroup.rotation.y += deltaTime * 1.2;
      this.holoIdeGroup.rotation.x = Math.sin(this.time) * 0.2;
      this.holoIdeGroup.position.y = 0.45 + bob;
    } else {
      this.holoFpsGroup.rotation.y += deltaTime * 1.5;
      this.holoFpsGroup.position.y = 0.45 + bob;
    }

    // Beam pulse
    (this.beamMesh.material as THREE.MeshBasicMaterial).opacity = 0.1 + Math.sin(this.time * 6) * 0.04;
  }
}
